/** Queries shared by the shell (sidebar, header) and the pages, so they hit one cache entry. */
import { useQuery } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";

import { portal } from "./api";
import type { Site } from "./types";

/**
 * Cache keys for data fetched by site id. Each starts with the organization id after the prefix so
 * nothing cached for one business can render under another, and none uses the `"domains"` prefix
 * that the organization's registered domains (`domainKeys`) own.
 */
/** How long a deleted website stays restorable. Rarely changes: read once a session. */
export function useSiteDeletionPolicy() {
  return useQuery({
    queryKey: ["site-deletion-policy"],
    queryFn: () => portal.siteDeletionPolicy(),
    staleTime: Infinity,
  });
}

export const siteKeys = {
  site: (orgId: string, siteId: string) => ["site", orgId, siteId] as const,
  deployments: (orgId: string, siteId: string) => ["site-deployments", orgId, siteId] as const,
  domains: (orgId: string, siteId: string) => ["site-domains", orgId, siteId] as const,
  analytics: (orgId: string, siteId: string, range: unknown) =>
    ["site-analytics", orgId, siteId, range] as const,
};

/** The current organization's dashboard summary (plan, usage, recent sites and deployments). */
export function useOverview() {
  const { orgId } = useOrganization();
  return useQuery({ queryKey: ["overview", orgId], queryFn: () => portal.overview(orgId) });
}

/** The workspace's Weavex website (external sites from Hosting are not it). */
export function primarySite(sites: Site[]): Site | undefined {
  const built = sites.filter((s) => s.kind !== "external");
  return built.find((s) => s.status === "active" && s.live_deployment_id) ?? built[0];
}

/** Every custom domain across the organization's websites (with the site each belongs to). */
export function useOrganizationDomains() {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: ["org-domains", orgId],
    queryFn: async () => {
      const { sites } = await portal.sites(orgId);
      const perSite = await Promise.all(
        sites.map(async (site) =>
          (await portal.domains(site.id)).domains.map((domain) => ({ site, domain })),
        ),
      );
      return { sites, domains: perSite.flat() };
    },
  });
}
